import { Link, useSearchParams } from "react-router-dom";
import { CUM, DA_XUAT_BAN, NHAP_MON, tenPhu } from "../data/glossary.js";
import { NOI_DUNG } from "../data/glossaryContent.js";
import DoanVan from "../components/DoanVan.jsx";
import ThuatNguLat from "../components/ThuatNguLat.jsx";
import { TOOLS } from "../data/tools.js";
import { normalize } from "../lib/workspace.js";
import { docChon, ghiChon, locVaDem } from "@shared/locMuc.js";
import { KhungHaiCot, NhomLoc, OTim, DongDem } from "../components/LocHaiCot.jsx";

/**
 * /thuat-ngu — trang tra cứu toàn bộ thuật ngữ.
 *
 * Hai cách dùng: tra một từ cụ thể (ô tìm kiếm là đường nhanh nhất) và học
 * từ đầu (khối "Mới bắt đầu?" gồm 10 khái niệm nền theo thứ tự nên đọc).
 * Mỗi mục là một hàng: tên, tên tiếng Anh/viết tắt, định nghĩa một câu luôn
 * hiện; mở ra xem giải thích, ví dụ và công cụ áp dụng ngay tại chỗ. Trang
 * riêng /thuat-ngu/<slug> vẫn giữ để chia sẻ và để các trang khác dẫn tới.
 */

const TEN_CUM = (k) => CUM[k] || k;
/** Tên ngắn và màu cho chip chủ đề, cùng kiểu chip trạm ở /cong-cu. */
const CHIP_CUM = {
  CO_BAN: ["Khái niệm nền", "#f4a623"],
  CO_PHAN: ["Cổ phần", "#e9a48e"],
  CONG_CU_VON: ["Điều khoản", "#c3b1e6"],
  DINH_GIA: ["Định giá", "#9fb7e0"],
  TAI_CHINH: ["Tài chính", "#86cfaa"],
  PHAP_LY: ["Pháp lý", "#7fc4c9"],
  QUY_TRINH: ["Quy trình", "#d6c27a"],
};
const mauCum = (k) => CHIP_CUM[k]?.[1] || "#a9bccd";

/** Xếp hạng kết quả: trùng tên > trùng tên khác > tên chứa từ khoá > định nghĩa chứa từ khoá. */
function diemKhop(x, q) {
  const ten = normalize(x.term);
  const khac = x.aliases.map(normalize);
  if (ten === q || khac.includes(q)) return 0;
  // Từ khoá ngắn (CAC, LTV, VC…) chỉ khớp nguyên từ, tránh "cac" khớp "cách".
  if (q.length <= 3) {
    const tu = (str) => str.split(/[^a-z0-9]+/).includes(q);
    if (ten.startsWith(q) || tu(ten) || khac.some(tu)) return 1;
    return -1;
  }
  if (ten.startsWith(q) || khac.some((a) => a.startsWith(q))) return 1;
  if (ten.includes(q) || khac.some((a) => a.includes(q))) return 2;
  if (normalize(x.shortDef).includes(q)) return 3;
  return -1;
}

function MoRong({ x }) {
  const nd = NOI_DUNG[x.slug];
  const dau = nd?.body?.[0];
  const viDu = nd?.body?.find((b) => /số cụ thể|ví dụ|bằng số/i.test(b.h));
  const congCu = x.relatedTools.map((id) => TOOLS.find((t) => t.toolId === id)).filter(Boolean).slice(0, 2);
  return (
    <div className="td-mo">
      {dau?.p?.[0] && (<><p className="td-mo-nhan">Hiểu đơn giản</p><p><DoanVan chu={dau.p[0]} /></p></>)}
      {viDu && viDu !== dau && viDu.p?.[0] && (<><p className="td-mo-nhan">Ví dụ</p><p><DoanVan chu={viDu.p[0]} /></p></>)}
      <p className="td-mo-lien-ket">
        <Link to={`/thuat-ngu/${x.slug}`}>Đọc giải thích đầy đủ →</Link>
        {congCu.map((t) => <Link key={t.toolId} to={`/cong-cu/${t.slug}`}>Mở công cụ: {t.title} →</Link>)}
      </p>
    </div>
  );
}

function HangThuatNgu({ x, hienCum }) {
  const phu = tenPhu(x);
  return (
    <li className="cct tdc" id={x.slug} style={{ "--mau": mauCum(x.cum) }}>
      <details>
        <summary>
          <span className="tdc-dau">
            <span className="cct-ten">{x.term}</span>
            {hienCum && <span className="tdc-cum"><i aria-hidden="true" />{CHIP_CUM[x.cum]?.[0] || TEN_CUM(x.cum)}</span>}
          </span>
          {phu && <span className="tdc-phu">{phu}</span>}
          <span className="cct-hoi">{x.shortDef}</span>
          <span className="cct-chan">
            <span className="cct-dung tdc-mo-nut">Ví dụ và cách áp dụng</span>
          </span>
        </summary>
        <MoRong x={x} />
      </details>
    </li>
  );
}

export default function Glossary() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const groupChon = docChon(params, "group"), toolChon = docChon(params, "tool");
  const change = (k, v) => {
    const p = new URLSearchParams(params);
    v ? p.set(k, v) : p.delete(k);
    p.delete("sort");
    setParams(p, { replace: true });
  };
  const doiNhom = (k) => (ds) => setParams(ghiChon(params, k, ds), { replace: true });
  const qn = normalize(q.trim());
  // Công cụ có dùng thuật ngữ, xếp theo số thuật ngữ nhiều nhất.
  const cacCongCu = TOOLS
    .map((t) => ({ t, so: DA_XUAT_BAN.filter((x) => x.relatedTools.includes(t.toolId)).length }))
    .filter((x) => x.so).sort((a, b) => b.so - a.so).map((x) => x.t);
  const { ketQua: daLoc, dem: demNhom } = locVaDem(
    DA_XUAT_BAN,
    [
      { key: "group", giaTri: (x) => [x.cum] },
      { key: "tool", giaTri: (x) => x.relatedTools },
    ],
    { group: groupChon, tool: toolChon },
    (x) => !qn || diemKhop(x, qn) >= 0,
  );
  const ketQua = qn
    ? daLoc.map((x) => [x, diemKhop(x, qn)])
        .sort(([a, da], [b, db]) => da - db || a.term.localeCompare(b.term, "vi"))
        .map(([x]) => x)
    : [...daLoc].sort((a, b) => a.term.localeCompare(b.term, "vi"));
  const dangLoc = Boolean(q || groupChon.length || toolChon.length);
  const coTrongTatCa = qn && (groupChon.length || toolChon.length) && !ketQua.length
    ? DA_XUAT_BAN.some((x) => diemKhop(x, qn) >= 0)
    : false;
  const nhapMon = NHAP_MON.map((s) => DA_XUAT_BAN.find((x) => x.slug === s)).filter(Boolean);
  // Không tìm, không lọc: nhóm theo chủ đề (tên nhóm hiện một lần trên đầu cụm).
  const theoCum = !qn
    ? Object.keys(CUM).map((k) => ({ k, ds: ketQua.filter((x) => x.cum === k) })).filter((n) => n.ds.length)
    : null;

  return (
    <>
      {/* Đầu trang: tiêu đề + ô tìm bên trái, thẻ lật 10 khái niệm nền bên phải
          (thay khối danh sách "Mới bắt đầu?" trước đây). */}
      <section className="td-dau tnl-dau">
        <div>
        <p className="eyebrow">TỪ ĐIỂN</p>
        <h1>Từ điển gọi vốn</h1>
        <p className="lead">{DA_XUAT_BAN.length} thuật ngữ, giải thích bằng tiếng Việt và ví dụ có số cụ thể.</p>
        <p className="td-dau-goi-y">Mới bắt đầu? Lật 10 khái niệm nền bên cạnh, theo thứ tự nên đọc.</p>
        </div>
        <ThuatNguLat ds={nhapMon} />
      </section>

      <KhungHaiCot
        soDangChon={groupChon.length + toolChon.length}
        boLoc={<>
          <NhomLoc tieuDe="Chủ đề" chon={groupChon} onDoi={doiNhom("group")} gioiHan={10}
            ds={Object.keys(CUM).map((k) => ({ v: k, nhan: CHIP_CUM[k]?.[0] || TEN_CUM(k), title: TEN_CUM(k), dem: demNhom.group[k] || 0, mau: mauCum(k) }))} />
          <NhomLoc tieuDe="Dùng trong công cụ" chon={toolChon} onDoi={doiNhom("tool")} gioiHan={6} moSan={toolChon.length > 0}
            ds={cacCongCu.map((t) => ({ v: t.toolId, nhan: t.title, dem: demNhom.tool[t.toolId] || 0 }))} />
        </>}
      >
        <OTim id="term-search" nhan="Tìm thuật ngữ" value={q} onChange={(v) => change("q", v)} placeholder="Tìm: runway, pha loãng, SAFE…" />
        <DongDem dangLoc={dangLoc} onXoa={() => setParams({}, { replace: true })}>
          Hiển thị <b>{ketQua.length}</b> trên {DA_XUAT_BAN.length} thuật ngữ{qn ? ", xếp theo độ khớp" : ", xếp A–Z theo chủ đề"}
        </DongDem>
        <div className="td-noi-dung">
          {theoCum ? (
            theoCum.map((n) => (
              <section key={n.k} className="td-nhom tdc-nhom" aria-labelledby={`cum-${n.k}`} style={{ "--mau": mauCum(n.k) }}>
                <h2 id={`cum-${n.k}`}><i aria-hidden="true" />{TEN_CUM(n.k)} <span>{n.ds.length}</span></h2>
                <ul className="cc-luoi tdc-luoi">{n.ds.map((x) => <HangThuatNgu key={x.slug} x={x} />)}</ul>
              </section>
            ))
          ) : (
            <ul className="cc-luoi tdc-luoi">{ketQua.map((x) => <HangThuatNgu key={x.slug} x={x} hienCum />)}</ul>
          )}

          {!ketQua.length && (
            <div className="empty-state">
              <h2>Chưa tìm thấy thuật ngữ</h2>
              {coTrongTatCa ? (
                <p>Không có trong lựa chọn này, nhưng có ở chủ đề khác.{" "}
                  <button type="button" className="nut phu" onClick={() => setParams(q ? { q } : {}, { replace: true })}>Tìm trong tất cả chủ đề</button>
                </p>
              ) : (
                <p>Thử tên tiếng Anh hoặc từ khoá ngắn hơn.{" "}
                  <a href="mailto:xin-chao@fundraising.vn?subject=%C4%90%E1%BB%81%20xu%E1%BA%A5t%20thu%E1%BA%ADt%20ng%E1%BB%AF">Đề xuất thuật ngữ</a>
                </p>
              )}
            </div>
          )}
        </div>
      </KhungHaiCot>
    </>
  );
}
